import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	Radio,
	RadioGroup,
	Stack,
	Typography,
} from "@mui/material";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
	BreadcrumbItem,
	BreadcrumbLink,
	Breadcrumbs,
	DataTable,
	DataTableCellProps,
	TemporalFormat,
	tryParseInstant,
} from "@variocube/app-ui";
import React, {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {BulkBookingOperation, useBookingApi, useBulkBookingOperationApi} from "../../api";
import {LocationTree} from "../../api/client.gen";
import {useLocalization, useWordingFlavor} from "@smalox/shared";
import {useTenantId} from "../../tenant";
import {queryKeys} from "../../utils/queryKeys";
import {LocationSelect} from "../location/LocationSelect";
import {View, ViewHeader} from "../View";

export function BulkEndBookings() {
	const {t} = useLocalization();
	const wordingFlavor = useWordingFlavor();
	const tenantId = useTenantId();
	const queryClient = useQueryClient();
	const {queryBookings} = useBookingApi();
	const {createBulkEndOperation, queryBulkBookingOperations, restoreBulkEndOperation} = useBulkBookingOperationApi();

	const [selectedLocation, setSelectedLocation] = useState<LocationTree | null>(null);
	const [resetLockStatus, setResetLockStatus] = useState(false);
	const [confirmEnd, setConfirmEnd] = useState(false);
	const [confirmAccess, setConfirmAccess] = useState(false);
	const [confirmLockReset, setConfirmLockReset] = useState(false);
	const [error, setError] = useState<any>(null);
	const [success, setSuccess] = useState<string | null>(null);

	// Reset confirmations when location changes
	useEffect(() => {
		setConfirmEnd(false);
		setConfirmAccess(false);
		setConfirmLockReset(false);
		setResetLockStatus(false);
		setSuccess(null);
		setError(null);
	}, [selectedLocation?.id]);

	// Reset lock confirmation when mode changes
	useEffect(() => {
		setConfirmLockReset(false);
	}, [resetLockStatus]);

	// Fetch open-lock booking count
	const {data: openLockCountData} = useQuery(
		["activeBookingCount", tenantId, selectedLocation?.id, "OPEN"],
		async () => {
			const result = await queryBookings({
				status: "ACTIVE",
				location: selectedLocation!.id,
				lockStatus: "OPEN",
				size: 0,
			});
			return result?.totalElements ?? 0;
		},
		{enabled: selectedLocation != null},
	);

	// Fetch all active booking count (any lock status)
	const {data: allActiveCountData} = useQuery(
		["activeBookingCount", tenantId, selectedLocation?.id, "ALL"],
		async () => {
			const result = await queryBookings({status: "ACTIVE", location: selectedLocation!.id, size: 0});
			return result?.totalElements ?? 0;
		},
		{enabled: selectedLocation != null},
	);

	// Fetch recent operations
	const {data: operationsData, isLoading: operationsLoading} = useQuery(
		queryKeys.bulkBookingOperations(tenantId),
		() => queryBulkBookingOperations({size: 10, sort: ["createdAt,desc"]}),
	);

	const openCount = openLockCountData ?? 0;
	const allCount = allActiveCountData ?? 0;
	const nonOpenCount = allCount - openCount;
	const count = resetLockStatus ? allCount : openCount;
	const locationSelected = selectedLocation != null;

	const endMutation = useMutation({
		mutationFn: ({locationId, resetLockStatus}: { locationId: number; resetLockStatus: boolean }) =>
			createBulkEndOperation({locationId, resetLockStatus}),
		onSuccess: (result) => {
			setSuccess(
				t(`bookings.bulkEnd.success.${wordingFlavor}`, {count: String(result?.bookingCount ?? 0)}),
			);
			setSelectedLocation(null);
			queryClient.invalidateQueries(queryKeys.bulkBookingOperations(tenantId));
		},
		onError: (e) => setError(e),
	});

	const restoreMutation = useMutation({
		mutationFn: (operationId: number) => restoreBulkEndOperation(operationId),
		onSuccess: (result) => {
			setSuccess(
				t(`bookings.bulkEnd.restoreSuccess.${wordingFlavor}`, {
					count: String(result?.bookingCount ?? 0),
				}),
			);
			queryClient.invalidateQueries(queryKeys.bulkBookingOperations(tenantId));
		},
		onError: (e) => setError(e),
	});

	const loading = endMutation.isLoading || restoreMutation.isLoading;
	const canSubmit = locationSelected && count > 0 && confirmEnd && confirmAccess
		&& (!resetLockStatus || confirmLockReset) && !loading;

	const handleSubmit = () => {
		if (!selectedLocation?.id) return;
		setError(null);
		setSuccess(null);
		endMutation.mutate({locationId: selectedLocation.id, resetLockStatus});
	};

	const handleRestore = (operationId: number) => {
		setError(null);
		setSuccess(null);
		restoreMutation.mutate(operationId);
	};

	return (
		<View>
			<Breadcrumbs>
				<BreadcrumbLink component={Link} to="..">
					{t(`bookings.title.${wordingFlavor}`)}
				</BreadcrumbLink>
				<BreadcrumbItem>{t(`bookings.bulkEnd.title.${wordingFlavor}`)}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader title={t(`bookings.bulkEnd.title.${wordingFlavor}`)} />

			{success && <Alert severity="success">{success}</Alert>}
			{error && <Alert severity="error">{error?.message ?? String(error)}</Alert>}

			<Card>
				<CardContent>
					<Stack spacing={3}>
						<LocationSelect
							value={selectedLocation}
							onChange={setSelectedLocation}
							label={t("locations.single")}
							fullWidth
						/>

						{!locationSelected && (
							<Typography color="text.secondary">
								{t("bookings.bulkEnd.selectLocation")}
							</Typography>
						)}

						{locationSelected && (
							<FormControl>
								<FormLabel>{t("bookings.bulkEnd.compartments")}</FormLabel>
								<RadioGroup
									value={resetLockStatus ? "all" : "open"}
									onChange={(e) => setResetLockStatus(e.target.value === "all")}
								>
									<FormControlLabel
										value="open"
										control={<Radio />}
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<span>{t(`bookings.bulkEnd.modeOpen.label.${wordingFlavor}`)}</span>
												<Chip size="small" label={openCount} />
											</Stack>
										}
									/>
									<FormHelperText sx={{mt: 0, ml: 4}}>
										{t(`bookings.bulkEnd.modeOpen.helper.${wordingFlavor}`)}
									</FormHelperText>
									<FormControlLabel
										value="all"
										control={<Radio />}
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<span>{t(`bookings.bulkEnd.modeAll.label.${wordingFlavor}`)}</span>
												<Chip size="small" label={allCount} />
												{nonOpenCount > 0 && (
													<Chip
														size="small"
														color="warning"
														label={t("bookings.bulkEnd.lockResetCount", {
															count: String(nonOpenCount),
														})}
													/>
												)}
											</Stack>
										}
									/>
									<FormHelperText sx={{mt: 0, ml: 4}}>
										{t(`bookings.bulkEnd.modeAll.helper.${wordingFlavor}`)}
									</FormHelperText>
								</RadioGroup>
							</FormControl>
						)}

						<FormControlLabel
							disabled={!locationSelected || count === 0}
							control={
								<Checkbox
									checked={confirmEnd}
									onChange={(e) => setConfirmEnd(e.target.checked)}
								/>
							}
							label={t(`bookings.bulkEnd.confirmEnd.${wordingFlavor}`)}
						/>
						<FormControlLabel
							disabled={!locationSelected || count === 0}
							control={
								<Checkbox
									checked={confirmAccess}
									onChange={(e) => setConfirmAccess(e.target.checked)}
								/>
							}
							label={t(`bookings.bulkEnd.confirmAccess.${wordingFlavor}`)}
						/>
						{resetLockStatus && (
							<FormControlLabel
								disabled={!locationSelected || count === 0}
								control={
									<Checkbox
										checked={confirmLockReset}
										onChange={(e) => setConfirmLockReset(e.target.checked)}
									/>
								}
								label={t(`bookings.bulkEnd.confirmLockReset.${wordingFlavor}`)}
							/>
						)}
						<Box>
							<Button
								variant="contained"
								color="error"
								disabled={!canSubmit}
								onClick={handleSubmit}
							>
								{t(`bookings.bulkEnd.submit.${wordingFlavor}`)}
							</Button>
						</Box>
					</Stack>
				</CardContent>
			</Card>

			<RecentOperations
				operations={operationsData?.content ?? []}
				loading={operationsLoading}
				onRestore={handleRestore}
			/>
		</View>
	);
}

interface RecentOperationsProps {
	operations: BulkBookingOperation[];
	loading: boolean;
	onRestore: (operationId: number) => void;
}

function OperationTypeCell({row}: DataTableCellProps<BulkBookingOperation>) {
	const {t} = useLocalization();
	return <>{row.type === "END" ? t("bookings.bulkEnd.typeEnd") : t("bookings.bulkEnd.typeRestore")}</>;
}

function OperationLocationCell({row}: DataTableCellProps<BulkBookingOperation>) {
	return <>{row.locationName}</>;
}

function OperationCountCell({row}: DataTableCellProps<BulkBookingOperation>) {
	return <>{row.bookingCount}</>;
}

function OperationCreatedAtCell({row}: DataTableCellProps<BulkBookingOperation>) {
	const createdAt = row.createdAt ? tryParseInstant(row.createdAt) : null;
	return createdAt ? <TemporalFormat value={createdAt} /> : null;
}

function OperationCreatedByCell({row}: DataTableCellProps<BulkBookingOperation>) {
	return <>{row.createdBy}</>;
}

function RecentOperations({operations, loading, onRestore}: RecentOperationsProps) {
	const {t} = useLocalization();

	const columns = useMemo(
		() => [
			{
				label: t("bookings.bulkEnd.operationType"),
				field: "type" as const,
				component: OperationTypeCell,
			},
			{
				label: t("locations.single"),
				field: "locationName" as const,
				component: OperationLocationCell,
			},
			{
				label: t("bookings.bulkEnd.bookingCount"),
				field: "bookingCount" as const,
				component: OperationCountCell,
			},
			{
				label: t("bookings.bulkEnd.createdAt"),
				field: "createdAt" as const,
				component: OperationCreatedAtCell,
			},
			{
				label: t("bookings.bulkEnd.createdBy"),
				field: "createdBy" as const,
				component: OperationCreatedByCell,
			},
			{
				label: "",
				field: "id" as const,
				component: function ActionCell({row}: DataTableCellProps<BulkBookingOperation>) {
					if (row.type === "END" && !row.restoredByOperationId) {
						return (
							<Button size="small" onClick={() => row.id && onRestore(row.id)}>
								{t("bookings.bulkEnd.restore")}
							</Button>
						);
					}
					if (row.type === "END" && row.restoredByOperationId) {
						return (
							<Typography variant="body2" color="text.secondary">
								{t("bookings.bulkEnd.restored")}
							</Typography>
						);
					}
					return null;
				},
			},
		],
		[t, onRestore],
	);

	if (operations.length === 0 && !loading) {
		return null;
	}

	return (
		<DataTable
			columns={columns}
			rows={operations}
			loading={loading}
		/>
	);
}
